import { useContext } from "react";
import { useIntl } from "react-intl";

import { useMutation } from "@tanstack/react-query";

import { Inputs } from "../../../api/inputs";
import { Plants } from "../../../api/plants";
import { CafsType } from "../../../consts/cafsType";
import { MutationKey } from "../../../consts/mutation-keys";
import { QueryKey } from "../../../consts/query-keys";
import { useApiError } from "../../../hooks/useApiError";
import { useApiSuccess } from "../../../hooks/useApiSuccess";
import { useFetchError } from "../../../hooks/useFetchError";
import { ApiError } from "../../../types/api-error";
import { ApiHookConfig } from "../../../types/api-hook-config";
import { CafsTreeNodeType } from "../../../types/cafs-tree-node-type";
import { DashboardContext } from "../DashboardContext";

type UseCafsAssignProps = ApiHookConfig & {
  cafs: CafsTreeNodeType | undefined;
};

export const useCafsAssign = ({ onSuccess, cafs }: UseCafsAssignProps) => {
  const { selectedDataset } = useContext(DashboardContext);
  const { formatMessage } = useIntl();

  const { handleSuccess } = useApiSuccess({
    snackbarText: formatMessage(
      {
        id: "ACTION.CAFS_ASSIGN_SUCCESS",
        defaultMessage:
          "Item {name} has been assigned to {dataset} successfully.",
        description: "Success message for CAFS assign to dataset",
      },
      {
        name: cafs?.name,
        dataset: selectedDataset?.Name,
      },
    ),
    invalidate: [[QueryKey.DataSetDetails]],
    onSuccessFn: onSuccess,
  });

  const { handleError } = useApiError();

  const mutationFn = () => {
    return cafs?.type === CafsType.Input
      ? Inputs.datasetAssign({
          path: { datasetId: +selectedDataset?.Id!, inputId: +cafs?.id! },
        })
      : Plants.datasetAssign({
          path: { datasetId: +selectedDataset?.Id!, plantId: +cafs?.id! },
        });
  };

  const {
    mutate: assignCafs,
    isPending: assignCafsPending,
    error,
  } = useMutation({
    mutationKey: [MutationKey.DatasetAssign, +selectedDataset!],
    mutationFn: mutationFn,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  useFetchError(error as unknown as ApiError);

  return { assignCafs, assignCafsPending };
};
